// Production-ready logging utility
// Can be extended with Sentry, LogRocket, or other services

export interface LogContext {
  component?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: any;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isTest = process.env.NODE_ENV === 'test';

  private formatMessage(level: string, message: string, context?: LogContext) {
    const timestamp = new Date().toISOString();
    const baseContext = {
      timestamp,
      level,
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context
    };

    return baseContext;
  }

  info(message: string, context?: LogContext) {
    if (this.isDevelopment || this.isTest) {
      console.info('ℹ️ INFO:', message, context);
    }
    
    // In production, send to logging service
    // Example: this.sendToLoggingService('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    if (this.isDevelopment || this.isTest) {
      console.warn('⚠️ WARN:', message, context);
    }
    
    // In production, send to logging service
    // const logData = this.formatMessage('warn', message, context);
    // Example: this.sendToLoggingService('warn', message, context);
  }

  error(message: string, error?: Error | any, context?: LogContext) {
    const errorContext = {
      ...context,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(error.status && { status: error.status })
      } : undefined
    };

    const logData = this.formatMessage('error', message, errorContext);
    
    // Always log errors in development
    if (this.isDevelopment || this.isTest) {
      console.error('❌ ERROR:', message, errorContext);
    }
    
    // In production, send to error monitoring service
    // Example: Sentry.captureException(error, { extra: logData });
    
    return logData;
  }

  debug(message: string, context?: LogContext) {
    if (this.isDevelopment) {
      console.debug('🐛 DEBUG:', message, context);
    }
  }

  // Helper method for API errors
  apiError(endpoint: string, error: any, context?: LogContext) {
    return this.error(`API Error: ${endpoint}`, error, {
      ...context,
      endpoint,
      component: 'API'
    });
  }

  // Helper method for component errors
  componentError(componentName: string, error: any, context?: LogContext) {
    return this.error(`Component Error: ${componentName}`, error, {
      ...context,
      component: componentName
    });
  }

  // Future: Send to external logging service
  private sendToLoggingService(_level: string, _message: string, _context?: LogContext) {
    // Implementation for production logging service
    // Examples:
    // - Sentry for error tracking
    // - LogRocket for session replay
    // - DataDog for comprehensive logging
    // - Custom analytics endpoint
  }
}

// Export singleton instance
export const logger = new Logger();
export default logger;